/**
 * 物件リクエスト: 入力 → 確認 → 完了（静的ビルド用 sessionStorage 連携）
 */
import { validatePropertyRequestForm, initPropertyRequestFormValidation } from './property-request-form-validation.js';
import { isRangePlaceholderValue } from './range-select-control.js';

const STORAGE_KEY = 'takamatsu_property_request_v1';
const CONFIRM_PATH = '/property_request_confirm.html';
const COMPLETE_PATH = '/property_request_complete.html';

/** デザイン確認用。本番では false にし、データ無し時は empty を表示 */
const PROPERTY_REQ_CONFIRM_PREVIEW_ALWAYS_VISIBLE = true;

const TYPE_LABELS = {
	new_house: '新築一戸建て',
	used_house: '中古一戸建て',
	mansion: '中古マンション',
	land: '土地',
};

const MOVE_LABELS = {
	now: '今すぐ',
	'3m': '3ヶ月以内',
	'6m': '6ヶ月以内',
	undecided: '未定',
};

function getFormRoot() {
	return document.querySelector('form[data-property-request-flow]#property-request-form');
}

function collectTypeDisplay(form) {
	const checks = form.querySelectorAll('input[name="preq_type[]"]:checked');
	const labels = [...checks].map((cb) => TYPE_LABELS[cb.value] || '').filter(Boolean);
	return labels.length ? labels.join('、') : '—';
}

function getSelectLabel(selectEl) {
	if (!selectEl || selectEl.selectedIndex < 0) return '';
	return selectEl.options[selectEl.selectedIndex].text.trim();
}

function collectBudgetDisplay(form) {
	const minEl = form.querySelector('#preq-budget-min');
	const maxEl = form.querySelector('#preq-budget-max');
	const minV = minEl ? minEl.value : '';
	const maxV = maxEl ? maxEl.value : '';
	if (isRangePlaceholderValue(minV) || isRangePlaceholderValue(maxV)) return '—';
	const minT = minEl ? getSelectLabel(minEl) : '';
	const maxT = maxEl ? getSelectLabel(maxEl) : '';
	if (!minT && !maxT) return '—';
	return `${minT} 〜 ${maxT}`;
}

function collectMoveDisplay(form) {
	const fd = new FormData(form);
	const v = (fd.get('preq_move_in') || '').toString();
	return MOVE_LABELS[v] || '—';
}

function collectRawFormFields(form) {
	const fd = new FormData(form);
	const pick = (name) => (fd.get(name) ?? '').toString();
	const checks = form.querySelectorAll('input[name="preq_type[]"]:checked');
	const types = [...checks].map((cb) => cb.value);
	return {
		preq_type: types,
		preq_area: pick('preq_area').trim(),
		preq_budget_min: pick('preq_budget_min'),
		preq_budget_max: pick('preq_budget_max'),
		preq_move_in: pick('preq_move_in'),
		preq_notes: pick('preq_notes'),
		preq_name_family: pick('preq_name_family').trim(),
		preq_name_given: pick('preq_name_given').trim(),
		preq_tel: pick('preq_tel').trim(),
		preq_email: pick('preq_email').trim(),
		preq_zip: pick('preq_zip').trim(),
		preq_pref: pick('preq_pref').trim(),
		preq_city: pick('preq_city').trim(),
		preq_street: pick('preq_street').trim(),
		preq_building: pick('preq_building').trim(),
	};
}

function collectFormPayload(form) {
	const fd = new FormData(form);
	const prefEl = form.querySelector('#preq-pref');
	const prefLabel =
		prefEl && prefEl.selectedIndex >= 0 ? prefEl.options[prefEl.selectedIndex].text.trim() : '';
	const family = (fd.get('preq_name_family') || '').toString().trim();
	const given = (fd.get('preq_name_given') || '').toString().trim();
	const nameFull = [family, given].filter(Boolean).join(' ') || '—';

	const zipRaw = (fd.get('preq_zip') || '').toString().trim();
	const postalMark = '〒';
	const zipDisplay = zipRaw
		? `${postalMark} ${zipRaw.replace(new RegExp(`^${postalMark}\\s*`), '')}`
		: '';

	const pref = (fd.get('preq_pref') || '').toString().trim();
	const city = (fd.get('preq_city') || '').toString().trim();
	const street = (fd.get('preq_street') || '').toString().trim();
	const building = (fd.get('preq_building') || '').toString().trim();
	const prefDisplay = prefLabel && prefLabel !== '都道府県' ? prefLabel : pref || '';

	const line2 = [prefDisplay, city, street].filter(Boolean).join(' ');
	const lines = [zipDisplay, line2, building].filter((line) => line && line.length > 0);
	const addressBlock = lines.length ? lines.join('\n') : '—';

	return {
		preq_type_display: collectTypeDisplay(form),
		preq_area: (fd.get('preq_area') || '').toString().trim() || '—',
		preq_budget_display: collectBudgetDisplay(form),
		preq_move_in_display: collectMoveDisplay(form),
		preq_notes: (fd.get('preq_notes') || '').toString().trim() || '—',
		preq_name_full: nameFull,
		preq_tel: (fd.get('preq_tel') || '').toString().trim() || '—',
		preq_email: (fd.get('preq_email') || '').toString().trim() || '—',
		preq_address_block: addressBlock,
	};
}

function saveDraft(payload) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* ignore */
	}
}

function loadDraft() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function clearDraft() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

function applyRestoreToForm(form, raw) {
	if (!raw || typeof raw !== 'object') return;

	if (Array.isArray(raw.preq_type)) {
		form.querySelectorAll('input[name="preq_type[]"]').forEach((cb) => {
			cb.checked = raw.preq_type.includes(cb.value);
		});
	}

	if (raw.preq_move_in != null && String(raw.preq_move_in) !== '') {
		const v = String(raw.preq_move_in);
		form.querySelectorAll('input[name="preq_move_in"]').forEach((radio) => {
			radio.checked = radio.value === v;
		});
	}

	const skip = new Set(['preq_type', 'preq_move_in']);
	for (const key of Object.keys(raw)) {
		if (skip.has(key)) continue;
		const val = raw[key];
		const str = val == null ? '' : String(val);
		const el = form.elements.namedItem(key);
		if (!el || el instanceof RadioNodeList) continue;
		if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			el.value = str;
		}
	}
}

function initFormPage() {
	const form = getFormRoot();
	if (!form) return;

	initPropertyRequestFormValidation(form);

	const draft = loadDraft();
	if (draft && draft._restore && typeof draft._restore === 'object') {
		applyRestoreToForm(form, draft._restore);
	}

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		if (!validatePropertyRequestForm(form)) {
			return;
		}
		const confirmPayload = collectFormPayload(form);
		const raw = collectRawFormFields(form);
		saveDraft({ ...confirmPayload, _restore: raw });
		window.location.href = CONFIRM_PATH;
	});
}

function setPreFormattedDesc(el, text) {
	if (!el) return;
	el.replaceChildren();
	const lines = String(text).split('\n');
	lines.forEach((line) => {
		const span = document.createElement('span');
		span.className = 'visit__confirmPreLine';
		span.textContent = line;
		el.appendChild(span);
	});
}

function setTextContent(el, text) {
	if (!el) return;
	el.textContent = text;
}

function initConfirmPage() {
	const page = document.querySelector('[data-property-request-page="confirm"]');
	if (!page) return;

	const block = document.getElementById('property-request-confirm-block');
	const empty = document.getElementById('property-request-confirm-empty');
	const shell = document.querySelector('.propertyRequest__confirmShell');
	const draft = loadDraft();

	if (!block || !empty) return;

	const sendBtn = document.getElementById('property-request-confirm-send');
	if (sendBtn) {
		sendBtn.addEventListener('click', () => {
			clearDraft();
			window.location.assign(COMPLETE_PATH);
		});
	}

	if (!draft) {
		if (PROPERTY_REQ_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			if (shell) shell.hidden = false;
			block.hidden = false;
			empty.hidden = true;
		} else {
			if (shell) shell.hidden = true;
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	const dash = '—';
	const hasRequired = draft.preq_name_full !== dash && draft.preq_email !== dash;

	if (!hasRequired) {
		if (PROPERTY_REQ_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			if (shell) shell.hidden = false;
			block.hidden = false;
			empty.hidden = true;
		} else {
			if (shell) shell.hidden = true;
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	if (shell) shell.hidden = false;
	block.hidden = false;
	empty.hidden = true;

	page.querySelectorAll('[data-preq-field]').forEach((el) => {
		const key = el.getAttribute('data-preq-field');
		if (!key || !(key in draft)) return;
		const val = draft[key];
		const out = typeof val === 'boolean' ? (val ? '同意済み' : dash) : String(val);
		if (el.classList.contains('visit__confirmDesc--pre')) {
			setPreFormattedDesc(el, out);
		} else {
			setTextContent(el, out);
		}
	});
}

export function initPropertyRequestFlow() {
	const pageType = document
		.querySelector('[data-property-request-page]')
		?.getAttribute('data-property-request-page');
	if (!pageType) return;
	if (pageType === 'form') initFormPage();
	if (pageType === 'confirm') initConfirmPage();
}
